import React, { useEffect, useState } from "react";
import {
  FaTimes,
  FaHistory,
  FaChevronLeft,
  FaChevronRight,
  FaEye,
  FaPlus,
  FaEdit,
  FaTrash,
  FaCheckCircle,
  FaUndo,
  FaSyncAlt,
} from "react-icons/fa";
import apiRequest from "../services/api";

export default function AuditLogModal({
  isOpen,
  onClose,
  entityId,
  title = "Audit History",
}) {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (isOpen && entityId) {
      fetchAudits();
    }
  }, [isOpen, entityId, page]);

  const fetchAudits = async () => {
    try {
      setLoading(true);
      setError("");

      const data = await apiRequest(`/reports/audit/${entityId}`, {
        params: {
          page,
          size,
          sort: "createdAt,desc",
        },
      });

      setAudits(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
    } catch (err) {
      console.error("Failed to fetch audit logs:", err);

      setError(err?.data?.message || "Failed to load audit history");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAudits([]);
    setExpandedId(null);
    setPage(0);
    setError("");

    onClose();
  };

  const parseJson = (value) => {
    if (!value) return null;

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";

    try {
      return new Date(date).toLocaleString();
    } catch {
      return date;
    }
  };

  const getActionStyle = (action = "") => {
    const value = action.toUpperCase();

    if (value.includes("CREATE") || value.includes("POST")) {
      return {
        icon: <FaPlus />,
        className: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
      };
    }

    if (
      value.includes("UPDATE") ||
      value.includes("EDIT") ||
      value.includes("CHANGE")
    ) {
      return {
        icon: <FaEdit />,
        className: "bg-blue-500/10 text-blue-400 border-blue-500/20",
      };
    }

    if (value.includes("DELETE") || value.includes("VOID")) {
      return {
        icon: <FaTrash />,
        className: "bg-red-500/10 text-red-400 border-red-500/20",
      };
    }

    if (value.includes("APPROVE") || value.includes("COMPLETE")) {
      return {
        icon: <FaCheckCircle />,
        className: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
      };
    }

    if (value.includes("REVERSE") || value.includes("REFUND")) {
      return {
        icon: <FaUndo />,
        className: "bg-orange-500/10 text-orange-400 border-orange-500/20",
      };
    }

    if (value.includes("VIEW")) {
      return {
        icon: <FaEye />,
        className: "bg-purple-500/10 text-purple-400 border-purple-500/20",
      };
    }

    return {
      icon: <FaHistory />,
      className: "bg-slate-500/10 text-slate-400 border-slate-500/20",
    };
  };

  const renderData = (value) => {
    if (!value) {
      return <span className="text-xs text-slate-500 italic">No data</span>;
    }

    const parsed = parseJson(value);

    if (typeof parsed === "string") {
      return (
        <pre className="text-xs text-slate-300 whitespace-pre-wrap break-all">
          {parsed}
        </pre>
      );
    }

    return (
      <div className="space-y-2">
        {Object.entries(parsed).map(([key, val]) => (
          <div
            key={key}
            className="flex justify-between gap-4 border-b border-white/[0.04] pb-1"
          >
            <span className="text-slate-500 text-xs">{key}</span>

            <span className="text-slate-200 text-xs font-mono text-right break-all">
              {val === null || val === undefined ? "-" : String(val)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
      {/* Modal */}
      <div
        className="relative w-full max-w-6xl max-h-[90vh]
                   overflow-hidden rounded-2xl
                   bg-[#0b1120]
                   border border-white/[0.08]
                   shadow-2xl"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4
                     border-b border-white/[0.08]"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl
                         bg-purple-500/10
                         border border-purple-500/20
                         flex items-center justify-center
                         text-purple-400"
            >
              <FaHistory />
            </div>

            <div>
              <h2 className="text-base font-bold text-white">{title}</h2>

              <p className="text-xs text-slate-400 mt-0.5">
                Complete transaction history and changes
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="w-9 h-9 rounded-lg
                       flex items-center justify-center
                       text-slate-400
                       hover:text-white
                       hover:bg-white/[0.06]
                       transition"
          >
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-150px)] p-6">
          {/* Summary */}
          <div className="flex items-center justify-between mb-5">
            <span className="text-xs text-slate-400">
              {loading
                ? "Loading..."
                : `${totalElements} audit record${
                    totalElements !== 1 ? "s" : ""
                  }`}
            </span>

            <button
              type="button"
              onClick={fetchAudits}
              disabled={loading}
              className="inline-flex items-center gap-2
                         px-3 py-2 rounded-lg
                         bg-white/[0.04]
                         hover:bg-white/[0.08]
                         border border-white/[0.06]
                         text-xs text-slate-300
                         transition disabled:opacity-50"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          {/* Error */}
          {error && (
            <div
              className="mb-5 px-4 py-3 rounded-xl
                         bg-red-500/10
                         border border-red-500/20
                         text-red-400 text-sm"
            >
              {error}
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="py-16 text-center text-slate-400 text-sm">
              Loading audit history...
            </div>
          )}

          {/* Empty */}
          {!loading && !error && audits.length === 0 && (
            <div className="py-16 text-center">
              <FaHistory className="mx-auto text-3xl text-slate-600 mb-3" />

              <p className="text-sm text-slate-400">No audit history found</p>
            </div>
          )}

          {/* Table */}
          {!loading && audits.length > 0 && (
            <div
              className="rounded-xl border border-white/[0.07]
                         overflow-hidden"
            >
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead
                    className="bg-white/[0.03]
                               border-b border-white/[0.07]"
                  >
                    <tr>
                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Action
                      </th>

                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        User
                      </th>

                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        Date & Time
                      </th>

                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                        IP Address
                      </th>

                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 text-right">
                        Details
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {audits.map((audit) => {
                      const style = getActionStyle(audit.action);

                      const isExpanded = expandedId === audit.id;

                      return (
                        <React.Fragment key={audit.id}>
                          <tr
                            className="border-b border-white/[0.05]
                                       hover:bg-white/[0.025]
                                       transition"
                          >
                            {/* Action */}
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center gap-2
                                           px-2.5 py-1 rounded-lg
                                           border text-[11px] font-bold
                                           ${style.className}`}
                              >
                                {style.icon}
                                {audit.action}
                              </span>
                            </td>

                            {/* User */}
                            <td className="px-4 py-3">
                              <div className="text-xs font-medium text-slate-200">
                                {audit.username || "System"}
                              </div>

                              {audit.userId && (
                                <div className="text-[10px] text-slate-500 mt-0.5">
                                  ID: {audit.userId}
                                </div>
                              )}
                            </td>

                            {/* Date */}
                            <td className="px-4 py-3">
                              <span className="text-xs text-slate-300">
                                {formatDate(audit.createdAt)}
                              </span>
                            </td>

                            {/* IP */}
                            <td className="px-4 py-3">
                              <span className="font-mono text-[11px] text-slate-400">
                                {audit.ipAddress || "-"}
                              </span>
                            </td>

                            {/* Details */}
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedId(isExpanded ? null : audit.id)
                                }
                                className="inline-flex items-center gap-2
                                           px-3 py-1.5 rounded-lg
                                           bg-white/[0.04]
                                           hover:bg-purple-500/10
                                           border border-white/[0.06]
                                           hover:border-purple-500/20
                                           text-xs text-slate-300
                                           hover:text-purple-300
                                           transition"
                              >
                                <FaEye />
                                {isExpanded ? "Hide" : "View"}
                              </button>
                            </td>
                          </tr>

                          {/* Expanded Before / After */}
                          {isExpanded && (
                            <tr>
                              <td
                                colSpan="5"
                                className="px-4 py-4
                                           bg-black/20
                                           border-b border-white/[0.05]"
                              >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {/* Before */}
                                  <div
                                    className="rounded-xl p-4
                                               bg-red-500/[0.04]
                                               border border-red-500/10"
                                  >
                                    <div className="flex items-center gap-2 mb-3">
                                      <span
                                        className="w-2 h-2 rounded-full
                                                   bg-red-400"
                                      />

                                      <h4 className="text-xs font-bold uppercase tracking-wider text-red-400">
                                        Before
                                      </h4>
                                    </div>

                                    {renderData(audit.beforeData)}
                                  </div>

                                  {/* After */}
                                  <div
                                    className="rounded-xl p-4
                                               bg-emerald-500/[0.04]
                                               border border-emerald-500/10"
                                  >
                                    <div className="flex items-center gap-2 mb-3">
                                      <span
                                        className="w-2 h-2 rounded-full
                                                   bg-emerald-400"
                                      />

                                      <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-400">
                                        After
                                      </h4>
                                    </div>

                                    {renderData(audit.afterData)}
                                  </div>
                                </div>

                                {/* Extra request information */}
                                <div
                                  className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3
                                             text-[11px]"
                                >
                                  <div className="p-3 rounded-lg bg-white/[0.025]">
                                    <span className="block text-slate-500 mb-1">
                                      Endpoint
                                    </span>

                                    <span className="font-mono text-slate-300 break-all">
                                      {audit.httpMethod} {audit.endpoint || "-"}
                                    </span>
                                  </div>

                                  <div className="p-3 rounded-lg bg-white/[0.025]">
                                    <span className="block text-slate-500 mb-1">
                                      Request ID
                                    </span>

                                    <span className="font-mono text-slate-300 break-all">
                                      {audit.requestId || "-"}
                                    </span>
                                  </div>

                                  <div className="p-3 rounded-lg bg-white/[0.025]">
                                    <span className="block text-slate-500 mb-1">
                                      Entity
                                    </span>

                                    <span className="font-mono text-slate-300">
                                      {audit.entityType || "-"} #
                                      {audit.entityId || "-"}
                                    </span>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-between mt-5">
              <span className="text-xs text-slate-500">
                Page {page + 1} of {totalPages}
              </span>

              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((current) => Math.max(0, current - 1))}
                  className="w-9 h-9 rounded-lg
                             flex items-center justify-center
                             border border-white/[0.07]
                             bg-white/[0.03]
                             text-slate-300
                             hover:bg-white/[0.08]
                             disabled:opacity-30
                             disabled:cursor-not-allowed
                             transition"
                >
                  <FaChevronLeft />
                </button>

                <button
                  type="button"
                  disabled={page >= totalPages - 1}
                  onClick={() =>
                    setPage((current) => Math.min(totalPages - 1, current + 1))
                  }
                  className="w-9 h-9 rounded-lg
                             flex items-center justify-center
                             border border-white/[0.07]
                             bg-white/[0.03]
                             text-slate-300
                             hover:bg-white/[0.08]
                             disabled:opacity-30
                             disabled:cursor-not-allowed
                             transition"
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
